<template>
    <!--
        XML Sitecore link data samples
        ignore 'text' and 'class' as we set those through fields or elsewhere

        internal xml
        <link
            text="Link text"
            anchor="Anchor"
            linkType="internal"
            class="Style"
            target="target"
            title="Alt text"
            queryString="Query
            string"
            id="Item id"
       />

        media xml
        <link
            text="Link text"
            linkType="media"
            title="Alt text"
            class="Style"
            target="Target"
            id="Item id"
       />

        external xml
        <link
            text="Link text"
            linkType="external"
            url="http://URL"
            anchor=""
            title="Alt text"
            class="Style"
            target="Target"
       />

        anchor xml
        <link
            text="Link text"
            linkType="anchor"
            url="anchor"
            anchor="anchor"
            title="Alt text"
            class="style"
       />

        mail xml
        <link
            text='Link text'
            linkType='mailto'
            style='style'
            url='mailto:email?subject=subject'
            title=''
       />
     -->
    <div class="option--link">
        <div class="option--link__inputs">
            <!-- <transition-group name="app-components"> -->

            <div class="option--link__input">
                <label for="link-type" class="option--link__input-label">
                    Link Type
                </label>

                <dropdown
                    id="link-type"
                    :value="updatedLinkData.linkType"
                    :options="linkTypeOptions"
                    placeholder="Select Type"
                    native
                    @change="switchLinkType"
                    @input="inputLinkType"
                />
            </div>

            <!-- Item path or URL -->
            <div v-if="isShowUrlType" key="UrlType" class="option--link__input">
                <label for="link-url" class="option--link__input-label">
                    Url
                </label>
                <input
                    v-if="isItemType"
                    id="link-path"
                    :value="updatedLinkData.href || 'No item selected'"
                    type="url"
                    disabled
                    @input="setChangedData(true)"
                >
                <input
                    v-else
                    id="link-url"
                    v-model="updatedLinkData.href"
                    :disabled="hasGeneratedUrl || isItemType"
                    type="url"
                    @input="setChangedData(true)"
                >
                <button
                    v-if="isItemType"
                    id="link-path-button"
                    class="option--link__button"
                    @click.prevent="selectItem"
                >
                    Select item
                </button>
            </div>
            <!-- Item path or URL end -->

            <!-- Email only -->
            <div v-if="isEmail" class="option--link__input">
                <label for="link-email" class="option--link__input-label">
                    Email
                </label>
                <input
                    id="link-email"
                    v-model="email"
                    type="email"
                    @change="setChangedData(true)"
                >
            </div>

            <div v-if="isEmail" class="option--link__input">
                <label for="link-subject" class="option--link__input-label">
                    Subject
                </label>
                <input
                    id="link-subject"
                    v-model="emailSubject"
                    type="text"
                    @change="setChangedData(true)"
                >
            </div>

            <div v-if="hasAnchor" class="option--link__input">
                <label for="link-anchor" class="option--link__input-label">
                    Anchor
                </label>
                <input
                    id="link-anchor"
                    v-model="updatedLinkData.anchor"
                    type="text"
                    @change="setChangedData(true)"
                >
            </div>

            <!-- Target v-model on custom field to keep on single var -->
            <div v-if="!isEmail" class="option--link__input">
                <label for="link-target" class="option--link__input-label">
                    Target
                </label>

                <dropdown
                    id="link-target"
                    :value="targetSelect"
                    :options="linkTargetOptions"
                    native
                    @change="switchLinkTarget"
                    @input="updateTarget"
                />
            </div>

            <div v-show="isCustomTarget && !isEmail" class="option--link__input">
                <label for="link-custom-target" class="option--link__input-label">
                    Custom target
                </label>

                <input
                    id="link-custom-target"
                    v-model="updatedLinkData.target"
                    type="text"
                    @change="setChangedData(true)"
                >
            </div>
            <!-- Target end -->

            <div v-if="isInternalType" class="option--link__input">
                <label for="link-query-string" class="option--link__input-label">
                    Query string
                </label>

                <input
                    id="link-query-string"
                    v-model="updatedLinkData.queryString"
                    type="text"
                    @change="setChangedData(true)"
                >
            </div>

            <!-- <transition-group /> -->
        </div>
    </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import {
    SET_SELECTED_LINK,
    CLEAR_SELECTED_LINK,
    SET_CHANGED_DATA,
    SET_SELECTED_MEDIA_TARGET,
    SHOW_DRAWER,
} from '@/store/types/mutation-types';

import { drawerHelperMixin } from '@/mixins/drawer-helper';
import mediaTargets from '@/data/constants/media-targets';

export default {
    name: 'link-builder',

    mixins: [drawerHelperMixin],

    data() {
        return {
            displayName: 'Link Builder',
            email: '',
            // eslint-disable-next-line no-useless-escape
            emailRegex: /mailto:([^\?]{0,254})/,
            emailSubject: '',
            emailSubjectRegex: /\?subject=([^]{0,256})/,
            initialTargetSet: true,
            linkId: '',
            linkTargetOptions: [
                { text: 'Active browser', value: '' },
                { text: 'New browser', value: '_blank' },
                { text: 'Custom', value: 'custom' },
            ],
            linkTypeOptions: [
                { text: 'Internal', value: 'internal' },
                { text: 'External', value: 'external' },
                { text: 'Media Library', value: 'media' },
                { text: 'Anchor', value: 'anchor' },
                { text: 'Email', value: 'mailto' },
            ],
            targetSelect: '',
            updatedLinkData: {},
        };
    },

    computed: {
        ...mapState({
            selectedComponent: state => state.page.selectedComponent,
            selectedContent: state => state.page.selectedContent,
            selectedLink: state => state.page.selectedLink,
            selectedMedia: state => state.page.selectedMedia,
            selectedMediaTarget: state => state.page.selectedMediaTarget,
        }),

        isInternalType() {
            return this.updatedLinkData.linkType === 'internal';
        },

        isItemType() {
            return this.updatedLinkData.linkType === 'internal'
                || this.updatedLinkData.linkType === 'media';
        },

        isShowUrlType() {
            return this.updatedLinkData.linkType === 'external'
                || this.updatedLinkData.linkType === 'internal'
                || this.updatedLinkData.linkType === 'media';
        },

        hasGeneratedUrl() {
            return this.updatedLinkData.linkType === 'mailto'
                || this.updatedLinkData.linkType === 'anchor';
        },

        isCustomTarget() {
            return this.targetSelect === 'custom';
        },

        isEmail() {
            return this.updatedLinkData.linkType === 'mailto';
        },

        hasAnchor() {
            return this.updatedLinkData.linkType === 'internal'
                || this.updatedLinkData.linkType === 'anchor';
        },

        emailHref() {
            const subjectQuery = this.emailSubject ? `?subject=${ this.emailSubject }` : '';
            return `mailto:${ this.email }${ subjectQuery }`;
        },
    },

    watch: {
        selectedContent(val) {
            if (!val) return;

            this.$set(this.updatedLinkData, 'href', val.url);
            this.$set(this.updatedLinkData, 'mediaId', `{${ val.id.toUpperCase() }}`);
            this.setChangedData(true);
        },

        selectedMedia(val) {
            if (!val || this.selectedMediaTarget !== mediaTargets.LINK) return;

            let href = val.url;

            // media links need to be converted to a relative URL, so they don't point to the CM
            if (this.updatedLinkData.linkType === 'media') {
                const urlObj = new URL(val.url);

                href = urlObj.pathname;
            }

            this.$set(this.updatedLinkData, 'href', href);
            this.$set(this.updatedLinkData, 'mediaId', `{${ val.id.toUpperCase() }}`);
            this.setChangedData(true);
        },

        selectedLink: {
            handler(val) {
                this.setData(val);
            },

            immediate: true,
        },

        updatedLinkData: {
            handler(val) {
                this.setSelectedLink({
                    linkId: this.linkId,
                    linkData: val,
                });
            },

            deep: true,
        },

        'updatedLinkData.anchor': function anchorWatch(val) {
            if (this.updatedLinkData.linkType === 'anchor') {
                // sitecore sets the values the same and adds # when building tag
                this.$set(this.updatedLinkData, 'href', val);
            }
        },

        email() {
            if (this.updatedLinkData.linkType === 'mailto') {
                this.$set(this.updatedLinkData, 'href', this.emailHref);
            }
        },

        emailSubject() {
            if (this.updatedLinkData.linkType === 'mailto') {
                this.$set(this.updatedLinkData, 'href', this.emailHref);
            }
        },
    },

    methods: {
        ...mapMutations({
            clearSelectedLink: CLEAR_SELECTED_LINK,
            showDrawer: SHOW_DRAWER,
            setSelectedLink: SET_SELECTED_LINK,
            setChangedData: SET_CHANGED_DATA,
            setSelectedMediaTarget: SET_SELECTED_MEDIA_TARGET,
        }),

        setData(val) {
            this.linkId = val.linkId;
            const tempLink = val.linkData;
            if (this.selectedComponent && this.selectedComponent.displayName === 'Skip Navigation') {
                tempLink.linkType = 'anchor';
            }
            this.updatedLinkData = tempLink || {};
            this.initialTargetSet = true;
            this.setTarget(); // because of select/input setup, need to set targetSelect

            if (this.updatedLinkData.linkType === 'mailto') {
                this.setEmail();
            }
        },

        setTarget() {
            if (!this.updatedLinkData.target) {
                this.targetSelect = '';
            } else if (this.updatedLinkData.target === '_blank') {
                this.targetSelect = '_blank';
            } else {
                this.targetSelect = 'custom';
            }
        },

        setEmail() {
            const emailMatch = this.emailRegex.exec(this.updatedLinkData.href);

            if (emailMatch) {
                this.email = emailMatch[1];
            }

            const emailSubjectMatch = this.emailSubjectRegex.exec(this.updatedLinkData.href);

            if (emailSubjectMatch) {
                this.emailSubject = emailSubjectMatch[1];
            }
        },

        inputLinkType(val) {
            this.updatedLinkData.linkType = val;
            this.setChangedData(true);
        },

        switchLinkType(val) {
            const oldVal = this.updatedLinkData.linkType;

            if ((oldVal === 'internal' && val !== 'internal')
                || (oldVal === 'external' && val !== 'external')
                || (oldVal === 'media' && val !== 'media')) {
                this.$set(this.updatedLinkData, 'href', '');
                this.$set(this.updatedLinkData, 'mediaId', '');
                return;
            }

            if (oldVal === 'mailto') {
                // clear email to remove mailto:
                this.email = '';
                this.$set(this.updatedLinkData, 'href', '');
                return;
            }

            if (val === 'mailto') {
                this.$set(this.updatedLinkData, 'href', this.emailHref);
                return;
            }

            if (oldVal === 'anchor') {
                // clear anchor url val
                this.$set(this.updatedLinkData, 'anchor', '');
                this.$set(this.updatedLinkData, 'href', '');
                return;
            }

            if (val === 'anchor') {
                this.$set(this.updatedLinkData, 'href', this.updatedLinkData.anchor);
                return;
            }
        },

        switchLinkTarget(val) {
            this.targetSelect = val;
            this.$set(this.updatedLinkData, 'target', val);
        },

        updateTarget(val) {
            this.updatedLinkData.target = val;
            this.setChangedData(true);
        },

        defaultLinkData() {
            return {
                id: '',
                linkType: 'internal',
                anchor: '',
                href: '',
                target: '',
                queryString: '',
                title: '',
                mediaId: '',
            };
        },

        selectItem() {
            if (this.updatedLinkData.linkType === 'internal') {
                this.showDrawer('navigation-tree-content-selector');
            } else if (this.updatedLinkData.linkType === 'media') {
                this.openMediaDrawer();
                this.setSelectedMediaTarget(mediaTargets.LINK);
            }
        },
    },
};
</script>

<style lang="scss">
@import '@/styles/_includes.scss';

.option--link {
    padding: 5px 20px;

    &__header {
        display: flex;
        justify-content: space-between;
    }

    &__label {
        margin-bottom: 0.25rem;
        display: block;
        font-weight: 700;
        font-size: 13px;
    }

    &__variant {
        font-size: 12px;
    }

    &__path {
        min-height: 10px;
        font-size: 14px;
    }

    &__inputs {
        width: 100%;
        padding: .25rem;
    }

    &__input {
        margin-bottom: 0.5rem;
    }

    &__input-label {
        display: block;
        width: 100%;
        font-size: 13px;
        text-align: left;
        font-weight: 700;
        margin-bottom: .5rem;
    }

    &__button {
        margin-top: 10px;
        min-width: 100%;
        height: 30px;
        color: var(--form-buttonColor);
        background: var(--form-buttonBackgroundColor);
        border-radius: 3px;
        font-size: 13px;

        &:hover {
            color: var(--form-buttonHoverColor);
            background: var(--form-buttonHoverBackgroundColor);
        }
    }

    input, select {
        height: 30px;
        font-size: 13px;
        width: 100%;
        padding: 5px;
        border-width: 1px;
        padding-top: .25rem;
        padding-bottom: .25rem;
        border-radius: .25rem;
    }

    input {
        color: black;
    }
}

</style>
